import Queue from "p-queue";
import { useState, useEffect, useMemo, useRef } from "react";
import { poll } from "@/lib/api";
import { processUpload, getUploadStatus, studyProcess, getStudyStatus, StudyAssetStatus } from "@/api/studio/uploads/api";
import { PreProcessedData } from "@/api/studio/data/types/pre-processed-data.type";
import { useEditorStore } from "@/features/studies/stores/use-editor-store";
import { AttentionReportsSkeleton } from "@/features/reports/components/attention-reports-skeleton";
import { StudiesLayout } from "@/components/layouts";
import { FullPageLoader } from "@/components/fallbacks";
import { useGlobalStore } from "@/stores/global-store";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { Progress } from "@/components/ui/progress";

/* Prop Types */
interface StudyAssetProcessRouteProps {
  projectId: string;
  studyId: string;
}

/**
 * StudyAssetProcessRoute component
 *
 * Processes the uploaded assets — routes to the legacy per-asset queue flow or the
 * study-level flow depending on the studyEdit1 feature flag.
 */
export const StudyAssetProcessRoute = ({ projectId, studyId }: StudyAssetProcessRouteProps) => {
  const isStudyEdit = useFeatureFlag("studyEdit1");

  return isStudyEdit ? <StudyAssetProcessStudyEdit projectId={projectId} studyId={studyId} /> : <StudyAssetProcessLegacy projectId={projectId} studyId={studyId} />;
};

/* ─── Legacy flow (studyEdit1 off) ─── */

const StudyAssetProcessLegacy = ({ projectId, studyId }: StudyAssetProcessRouteProps) => {
  /* Stores */
  const entitlements = useGlobalStore((state) => state.entitlements);

  /* State */
  const [status, setStatus] = useState("waiting");
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const allAssetRegionData = useEditorStore((state) => state.allAssetRegionData);
  const contextPackIds = useEditorStore((state) => state.contextPackIds);

  /* Hooks */
  const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-process");

  /* Refs */
  const videoRef = useRef<HTMLVideoElement>(null);

  /* Actions */
  const { setContextPackIds } = useEditorStore((state) => state.editorActions);

  /* Queue */
  const queue = useMemo(() => new Queue({ concurrency: 5 }), []);

  queue.on("active", () => {
    setStatus("active");
  });

  queue.on("empty", () => {
    setStatus("empty");
  });

  queue.on("error", () => {
    setStatus("error");
  });

  queue.on("idle", () => {
    // Reset the context pack IDs in the editor store
    setContextPackIds([]);

    navigateToNext();
  });

  const worker = async (data: PreProcessedData) => {
    await processUpload(projectId, studyId, data, contextPackIds).then(async ({ taskId }) => {
      return await poll(() => getUploadStatus(projectId, studyId, taskId));
    });
  };

  /* Side Effects */
  useEffect(() => allAssetRegionData.forEach((asset) => queue.add(() => worker(asset))), []);

  /* Hooks */
  // Check if user has access to Memory & Emotion features
  const hasMemoryEmotion = entitlements.some((entitlement) => entitlement.name === "memoryEmotion");

  // Messages configuration based on M&E access
  const messages = hasMemoryEmotion
    ? [
        { metric: "Attention", color: "text-[#19D3C8]", description: "Analysing assets..." },
        { metric: "Emotion", color: "text-primary", description: "Running algorithm..." },
        { metric: "Memory", color: "text-[#9747FF]", description: "Finalising..." },
      ]
    : [
        { metric: "Analyzing", color: "", description: "Analyzing scores..." },
        { metric: "Building", color: "", description: "Building graphs..." },
        { metric: "Running", color: "", description: "Running algorithm..." },
      ];

  // Different animation based on M&E access
  const animationSrc = hasMemoryEmotion ? "/videos/process-animation-m&e.webm" : "/videos/process-animation.webm";

  // Sync text with video animation using video timeupdate event
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const { currentTime, duration } = video;
      if (!duration) return;

      // Divide video duration by number of messages to determine which message to show
      const segmentDuration = duration / messages.length;
      const newIndex = Math.floor(currentTime / segmentDuration) % messages.length;

      setCurrentMessageIndex(newIndex);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, [messages]);

  const currentMessage = messages[currentMessageIndex];

  // Helper function to render header text with colored keywords
  const renderHeaderText = () => {
    if (!hasMemoryEmotion) {
      return <h2>{currentMessage.description}</h2>;
    }

    return (
      <h2>
        Getting <span className={`${currentMessage.color} transition-colors duration-200`}>{currentMessage.metric}</span> scores
      </h2>
    );
  };

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="reports">
      {(status === "active" || status === "empty") && (
        <FullPageLoader>
          <div className="flex flex-col justify-center gap-2 items-center">
            <video ref={videoRef} src={animationSrc} className={`${hasMemoryEmotion ? "w-60" : "w-48"} pb-2`} autoPlay muted loop />
            {renderHeaderText()}
            <p>{currentMessage.description}</p>
          </div>
        </FullPageLoader>
      )}
      <AttentionReportsSkeleton title={hasMemoryEmotion ? "Creative Impact" : "Attention"} />
    </StudiesLayout>
  );
};

/* ─── New flow (studyEdit1 on) ─── */

const POLL_INTERVAL = 3000;

const StudyAssetProcessStudyEdit = ({ projectId, studyId }: StudyAssetProcessRouteProps) => {
  /* Stores */
  const entitlements = useGlobalStore((state) => state.entitlements);

  /* State */
  const [assetStatuses, setAssetStatuses] = useState<StudyAssetStatus[]>([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const hasStarted = useRef(false);

  /* Refs */
  const videoRef = useRef<HTMLVideoElement>(null);

  /* Hooks */
  const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-process");

  /* Actions */
  const { setContextPackIds } = useEditorStore((state) => state.editorActions);

  /* Derived state */
  const succeededCount = assetStatuses.filter((a) => a.status === "SUCCEEDED").length;
  const totalCount = assetStatuses.length;

  /* Side Effects */
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const startProcessing = async () => {
      setIsActive(true);

      await studyProcess(projectId, studyId);

      // Poll until all assets reach a terminal state (SUCCEEDED or FAILED)
      while (true) {
        const statuses = await getStudyStatus(projectId, studyId, "process");
        setAssetStatuses(statuses);

        const isFinished = (status: string) => status === "SUCCEEDED" || status === "FAILED";
        if (statuses.length > 0 && statuses.every((a) => isFinished(a.status))) {
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }

      // Reset the context pack IDs in the editor store
      setContextPackIds([]);

      navigateToNext();
    };

    void startProcessing();
  }, []);

  // Check if user has access to Memory & Emotion features
  const hasMemoryEmotion = entitlements.some((entitlement) => entitlement.name === "memoryEmotion");

  // Messages configuration based on M&E access
  const messages = hasMemoryEmotion
    ? [
        { metric: "Attention", color: "text-[#19D3C8]", description: "Analysing assets..." },
        { metric: "Emotion", color: "text-primary", description: "Running algorithm..." },
        { metric: "Memory", color: "text-[#9747FF]", description: "Finalising..." },
      ]
    : [
        { metric: "Analyzing", color: "", description: "Analyzing scores..." },
        { metric: "Building", color: "", description: "Building graphs..." },
        { metric: "Running", color: "", description: "Running algorithm..." },
      ];

  // Different animation based on M&E access
  const animationSrc = hasMemoryEmotion ? "/videos/process-animation-m&e.webm" : "/videos/process-animation.webm";

  // Sync text with video animation using video timeupdate event
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const { currentTime, duration } = video;
      if (!duration) return;

      const segmentDuration = duration / messages.length;
      const newIndex = Math.floor(currentTime / segmentDuration) % messages.length;

      setCurrentMessageIndex(newIndex);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, [messages]);

  const currentMessage = messages[currentMessageIndex];

  const renderHeaderText = () => {
    if (!hasMemoryEmotion) {
      return (
        <h2>
          Processing {succeededCount} of {totalCount || "..."}
        </h2>
      );
    }

    return (
      <h2>
        Getting <span className={`${currentMessage.color} transition-colors duration-200`}>{currentMessage.metric}</span> scores
      </h2>
    );
  };

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="reports">
      {isActive && (
        <FullPageLoader>
          <div className="flex flex-col justify-center gap-2 items-center">
            <video ref={videoRef} src={animationSrc} className={`${hasMemoryEmotion ? "w-60" : "w-48"} pb-2`} autoPlay muted loop />
            {renderHeaderText()}
            {totalCount > 0 && <Progress value={(succeededCount / totalCount) * 100} staticColorClass="bg-success" className="w-1/2" />}
            <p>{currentMessage.description}</p>
          </div>
        </FullPageLoader>
      )}
      <AttentionReportsSkeleton title={hasMemoryEmotion ? "Creative Impact" : "Attention"} />
    </StudiesLayout>
  );
};
